
import { Suspense, lazy, useState } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { NavBar } from "@/components/NavBar";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Trash2, Edit } from "lucide-react";
import { useDrafts } from "@/hooks/use-drafts";
import { formatDistanceToNow, format } from "date-fns";
import { DraftsTableSkeleton } from "@/components/dashboard/DraftsTableSkeleton";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Footer = lazy(() => import("@/components/Footer"));

type Draft = {
  id: string;
  topics: {
    task_type: number;
    topic: string;
  };
  evaluations?: Array<{
    overall_score: number;
  }>;
  updated_at: string;
  is_submitted: boolean;
};

const BackButton = () => {
  const navigate = useNavigate();

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => navigate("/dashboard")}
      className="hover:bg-transparent"
    >
      <ChevronLeft className="h-5 w-5 text-stone-500 dark:text-white" />
      <span className="text-stone-500 dark:text-white">Back</span>
    </Button>
  );
};

const DraftTableHeader = () => (
  <thead className="bg-stone-100 dark:bg-stone-800">
    <tr>
      <th scope="col" className="px-4 md:px-8 py-4 text-left text-sm font-semibold text-stone-500">Topic</th>
      <th scope="col" className="px-4 md:px-8 py-4 text-left text-sm font-semibold text-stone-500">Score</th>
      <th scope="col" className="px-4 md:px-8 py-4 text-left text-sm font-semibold text-stone-500 min-w-[120px]">Date</th>
      <th scope="col" className="px-4 md:px-8 py-4" aria-label="Actions"></th>
    </tr>
  </thead>
);

const DraftTableRow = ({
  draft,
  taskType,
  onDeleteDraft
}: {
  draft: Draft;
  taskType: number;
  onDeleteDraft: (draftId: string) => void;
}) => {
  const navigate = useNavigate();
  const handleRowClick = () => navigate(`/practice?task=${taskType}&draft=${draft.id}`);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const confirmDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    onDeleteDraft(draft.id);
    setIsDeleteDialogOpen(false);
  };

  return (
    <>
      <tr
        onClick={handleRowClick}
        className="cursor-pointer border-t border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 transition-colors group hover:bg-stone-50 dark:hover:bg-stone-700"
      >
        <td className="px-4 md:px-8 py-4 text-sm font-medium group-hover:text-orange-500 dark:text-white">
          {draft.topics.topic}
        </td>
        <td className="px-4 md:px-8 py-4 text-sm font-medium group-hover:text-orange-500 dark:text-white">
          {draft.evaluations?.[0]?.overall_score || '-'}
        </td>
        <td className="px-4 md:px-8 py-4 text-sm font-medium group-hover:text-orange-500 dark:text-white">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className="cursor-pointer">
                {formatDistanceToNow(new Date(draft.updated_at), { addSuffix: true })}
              </TooltipTrigger>
              <TooltipContent>
                {format(new Date(draft.updated_at), 'yyyy-MM-dd HH:mm')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </td>
        <td className="px-4 md:px-8 py-4">
          <div className="flex justify-end gap-2">
            <Button
              variant="link"
              size="icon"
              className="text-stone-900 transition-colors hover:text-orange-500 dark:text-white"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/practice?task=${taskType}&draft=${draft.id}`);
              }}
              aria-label="Edit draft"
            >
              <Edit className="h-4 w-4" />
            </Button>
            {!draft.is_submitted && (
              <Button
                variant="link"
                size="icon"
                className="text-stone-900 transition-colors hover:text-orange-500 dark:text-white"
                onClick={confirmDelete}
                aria-label="Delete draft"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </td>
      </tr>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="dark:bg-stone-800 dark:border-stone-700">
          <DialogHeader>
            <DialogTitle className="dark:text-white">Delete Draft</DialogTitle>
            <DialogDescription className="dark:text-stone-500">
              Are you sure you want to delete this draft? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              className="dark:border-stone-700 dark:bg-stone-800 dark:text-white dark:hover:bg-stone-700"
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const DraftsTable = ({
  drafts,
  taskType,
  onDeleteDraft
}: {
  drafts: Draft[];
  taskType: number;
  onDeleteDraft: (draftId: string) => void;
}) => (
  <div className="overflow-hidden rounded-xl border border-stone-200 dark:border-stone-700">
    <table className="w-full" role="table">
      <DraftTableHeader />
      <tbody>
        {drafts.map((draft) => (
          <DraftTableRow
            key={draft.id}
            draft={draft}
            taskType={taskType}
            onDeleteDraft={onDeleteDraft}
          />
        ))}
      </tbody>
    </table>
  </div>
);

const AllDrafts = () => {
  const [searchParams] = useSearchParams();
  const taskType = Number(searchParams.get("task")) || 1;

  const { data: drafts = [], isLoading, refetch } = useDrafts({
    orderBy: 'updated_at',
    orderDirection: 'desc'
  });

  const filteredDrafts = drafts.filter(draft => draft.topics.task_type === taskType);

  const handleDeleteDraft = async (draftId: string) => {
    try {
      const { error } = await supabase
        .from("drafts")
        .delete()
        .eq("id", draftId);

      if (error) throw error;

      toast.success("Draft deleted successfully");
      refetch();
    } catch (error) {
      console.error("Error deleting draft:", error);
      toast.error("Failed to delete draft");
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-stone-50 dark:bg-stone-950">
      <NavBar />

      <main
        className="flex-grow px-4 py-8 max-w-6xl container mx-auto space-y-8"
        aria-labelledby="page-title"
      >
        <div className="flex items-center gap-4">
          <BackButton />
        </div>

        <h1 id="page-title" className="text-3xl font-semibold dark:text-white">
          Task {taskType} drafts
        </h1>

        {isLoading ? (
          <DraftsTableSkeleton />
        ) : (
          <DraftsTable
            drafts={filteredDrafts}
            taskType={taskType}
            onDeleteDraft={handleDeleteDraft}
          />
        )}
      </main>

      <Suspense fallback={<div className="h-16" />}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default AllDrafts;
